import { useEffect, useRef, useState } from 'react';

import type { FrameRect } from '@src/utility/frameGeometry';

/**
 * Track an element's viewport rect (`getBoundingClientRect`).
 *
 * Updates on:
 *  - element ref change
 *  - element size changes (`ResizeObserver`)
 *  - any scroll on any ancestor (`scroll` event, capture phase) — needed when
 *    the element is inside a scrollable container that doesn't itself resize
 *  - window resize
 *
 * Scroll/resize handlers are coalesced via `requestAnimationFrame` so a
 * fast-scrolling user fires at most one measurement per paint, not one per
 * scroll tick. `ResizeObserver` already coalesces, so it doesn't go through the
 * rAF gate.
 *
 * Returns `null` until a non-zero rect has been measured, so callers can render
 * nothing rather than render at the wrong place. A zero-area rect also reports
 * `null` — an element with no layout yet is not a position worth painting on.
 *
 * Viewport space specifically, because the consumers portal to `document.body`
 * rather than mounting inside the element's own container — that being the whole
 * point of the portal: bypass the player iframe's stacking context. Pair with
 * the converters in `@src/utility/frameGeometry` to map frame-relative
 * coordinates onto the measured rect.
 */
export function useViewportRect(
  element: HTMLElement | null | undefined,
): FrameRect | null {
  const [rect, setRect] = useState<FrameRect | null>(null);
  const rafIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!element) {
      setRect(null);
      return;
    }

    const measure = () => {
      const r = element.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) {
        setRect(null);
        return;
      }
      setRect({ left: r.left, top: r.top, width: r.width, height: r.height });
    };

    const scheduleMeasure = () => {
      if (rafIdRef.current !== null) return;
      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;
        measure();
      });
    };

    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(element);

    window.addEventListener('scroll', scheduleMeasure, true);
    window.addEventListener('resize', scheduleMeasure);

    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      ro.disconnect();
      window.removeEventListener('scroll', scheduleMeasure, true);
      window.removeEventListener('resize', scheduleMeasure);
    };
  }, [element]);

  return rect;
}
